import { Header } from './Header';
import { useState, useEffect, useRef } from 'react';
import {
  BarChart3,
  Bot,
  Brain,
  Zap,
  FileText,
  Briefcase,
  ArrowRight,
} from 'lucide-react';

interface Service {
  id: string;
  title: string;
  tagline: string;
  description: string;
  longDescription: string;
  icon: any;
  features: { title: string; description: string }[];
  benefits: string[];
}

const services: Service[] = [
  {
    id: 'power-platform',
    title: 'Power Platform Solutions',
    tagline: 'Low-code platforms, engineered for project controls',
    description:
      'Unlock the full potential of Microsoft Power Platform to automate processes, build custom applications, and drive digital transformation across your organisation.',
    longDescription:
      "Our Power Platform engineers ship tailored solutions that empower teams to innovate faster, automate repetitive tasks, and create sophisticated business applications without extensive coding. From initial strategy to deployment and ongoing support, we make sure your Power Platform investment delivers measurable ROI.",
    icon: Zap,
    features: [
      { title: 'Utility Power Apps Development', description: 'Custom mobile and web applications built with Power Apps to solve specific business challenges and streamline operations.' },
      { title: 'AI-Driven Power Apps', description: 'Intelligent applications enhanced with AI capabilities including predictive analytics, natural language processing, and automated decision-making.' },
      { title: 'Process Automation with Power Automate', description: 'Eliminate manual tasks, accelerate business processes, and create seamless workflows.' },
      { title: 'Custom Business Applications', description: 'Enterprise-grade applications tailored to your unique workflows, integrated with your existing systems.' },
      { title: 'Power Platform Consulting', description: 'Strategic guidance on Power Platform adoption, governance, best practices, and digital transformation roadmaps.' },
      { title: 'Integration & Modernisation', description: 'Connect disparate systems, modernise legacy applications, unify workflows across your stack.' },
    ],
    benefits: [
      'Accelerate digital transformation with low-code solutions',
      'Ship business apps in weeks, not quarters',
      'Empower citizen developers to build solutions',
      'Seamless integration with the Microsoft 365 ecosystem',
      'Enterprise-grade security and compliance',
    ],
  },
  {
    id: 'data-analytics',
    title: 'Data Analytics & Business Intelligence',
    tagline: 'Turn data into your competitive advantage',
    description:
      'Transform raw data into actionable insights with advanced analytics solutions powered by cutting-edge BI tools and data science expertise.',
    longDescription:
      'We help organisations harness the power of their data through comprehensive analytics strategies, from exploratory analysis to predictive modelling. Real-time visibility into business performance, enabling data-driven decision-making at every level.',
    icon: BarChart3,
    features: [
      { title: 'Microsoft Power BI Solutions', description: 'Interactive dashboards and reports built on Power BI, Power Platform, and Azure.' },
      { title: 'Google Cloud Analytics', description: 'Advanced analytics using Looker, GCP, and BigQuery for scalable data processing.' },
      { title: 'Exploratory Data Analysis', description: 'Deep dive into your data to uncover patterns, trends, and insights that drive strategy.' },
      { title: 'Real-Time Interactive Dashboards', description: 'Live monitoring solutions providing instant visibility into key business metrics.' },
      { title: 'Statistical Analysis & Modelling', description: 'Advanced methods to validate hypotheses, predict outcomes, and quantify business impact.' },
      { title: 'Custom KPI Frameworks', description: 'Performance metrics and dashboards aligned with your strategic objectives.' },
    ],
    benefits: [
      'Make faster, data-driven decisions',
      'Identify hidden opportunities and risks',
      'Lift operational efficiency across day-to-day workflows',
      'Real-time business performance visibility',
      'Democratise data access across the organisation',
    ],
  },
  {
    id: 'automated-reporting',
    title: 'Automated Reporting Systems',
    tagline: 'Eliminate manual reporting, amplify insight',
    description:
      'Streamline reporting processes with intelligent automation that delivers consistent, accurate reports on schedule, every time.',
    longDescription:
      'Tiered reporting solutions on Azure, Power BI, and Power Apps that create self-service reporting ecosystems scaling with your business.',
    icon: FileText,
    features: [
      { title: 'Google Cloud Workspace Integration', description: 'Automated reporting on Looker, GCP, and BigQuery for Google Workspace organisations.' },
      { title: 'Microsoft Workspace Solutions', description: 'Reporting built on Power BI, Power Platform, and Azure for Microsoft 365.' },
      { title: 'Bronze Tier · Core Automation', description: 'Foundation tier with automated data pipelines, scheduled reports, essential dashboards.' },
      { title: 'Silver Tier · Advanced Platform', description: 'Enhanced automation with Power Apps integration, custom workflows, self-service analytics.' },
      { title: 'AI-Driven Insights Engine', description: 'ML algorithms that detect anomalies, predict trends, surface actionable insights, generate narratives.' },
      { title: 'Custom Report Generation', description: 'Automated ETL pipelines with templated and ad-hoc reporting for different stakeholder needs.' },
    ],
    benefits: [
      'Free up hours of weekly reporting effort',
      'Eliminate human error in data processing',
      'Enable self-service analytics for all users',
      'Deliver reports on a continuous schedule, without manual intervention',
      'Scale reporting without adding headcount',
    ],
  },
  {
    id: 'ai-ml',
    title: 'AI & Machine Learning',
    tagline: 'Harness the power of artificial intelligence',
    description:
      'Cutting-edge AI and ML solutions that transform your business processes with intelligent automation and predictive capabilities.',
    longDescription:
      'From computer vision to natural language processing, we build production-ready AI models that solve real business problems.',
    icon: Brain,
    features: [
      { title: 'Agentic RAG Systems', description: 'Retrieval-Augmented Generation with LangGraph orchestration, combining structured and unstructured data sources.' },
      { title: 'AI Narrative Writer', description: 'Automated report generation that creates well-written narratives from data.' },
      { title: 'EWN Agent', description: 'Detects programme delays, checks contract clauses, and generates ready-to-raise Early Warning Notices.' },
      { title: 'ML-Based Delay Prediction', description: 'Models that predict project delays from historical patterns, resource constraints, and external factors.' },
      { title: 'ML-Based Cost Estimation', description: 'Cost prediction models that improve accuracy by learning from historical project data.' },
      { title: 'Computer Vision Solutions', description: 'Image classification and visual inspection for quality control, asset recognition, monitoring.' },
    ],
    benefits: [
      'Automate complex decision-making',
      'Predict outcomes with high accuracy',
      'Process unstructured data at scale',
      'Reduce operational costs',
      'Gain competitive advantage through AI',
    ],
  },
  {
    id: 'ai-workers',
    title: 'AI Workers & Intelligent Chatbots',
    tagline: 'Your 24/7 digital workforce',
    description:
      'Intelligent automation and conversational AI that enhance customer experience and operational efficiency.',
    longDescription:
      'AI-powered virtual assistants and chatbots that understand context, learn from interactions, and provide human-like support, integrated with your knowledge base and systems.',
    icon: Bot,
    features: [
      { title: 'Knowledge Management · Company Brain', description: "Flagship agentic RAG chatbot. Detects policy contradictions, fetches procedures, accelerates onboarding, provides 24/7 access to company expertise." },
      { title: 'Agentic RAG Systems', description: 'LangGraph orchestration with query classification, context aggregation, multi-source retrieval.' },
      { title: 'Custom RAG Architecture', description: 'Query classifier routes to structured (SQLite/Graph DB) or unstructured (Vector DB) queries, with hybrid support.' },
      { title: 'Intelligent AI Agents', description: 'Autonomous agents that perform tasks, analyse documents, and generate outputs like EWNs.' },
      { title: 'Organisation Knowledge Integration', description: 'Connect AI systems to knowledge bases, project data, CRM, enterprise systems.' },
      { title: 'Custom Chatbot Development', description: 'Conversational AI matched to your brand voice, handling complex multi-turn dialogue.' },
      { title: 'Multi-Source Citation System', description: 'Responses cite multiple data sources ensuring transparency and traceability.' },
    ],
    benefits: [
      'Always-on access to company knowledge',
      'Accelerate onboarding and reduce training time',
      'Detect policy contradictions automatically',
      'Near-instant responses for routine queries',
      'Handle unlimited concurrent conversations',
      'Free up staff for high-value activities',
    ],
  },
  {
    id: 'digital-pmo',
    title: 'Digital PMO Solutions',
    tagline: 'Modern project management for the digital age',
    description:
      'PMO solutions that provide visibility, control, and strategic alignment across your entire portfolio.',
    longDescription:
      'Streamline project delivery, improve resource utilisation, and provide executive visibility. Planning, execution, and reporting in one unified system.',
    icon: Briefcase,
    features: [
      { title: 'Live P6 · Report on a Page', description: 'Real-time Primavera P6 project status consolidated into a single comprehensive dashboard.' },
      { title: 'Full Digital Project Management', description: 'End-to-end lifecycle management with built-in best practices and integrated workflows.' },
      { title: 'Advanced Schedule Management', description: 'Visual timelines, Gantt charts, critical path analysis, and P6 integration.' },
      { title: 'Resource & Budget Management', description: 'Intelligent allocation, capacity planning, real-time budget tracking, variance analysis.' },
      { title: 'Risk Assessment & Mitigation', description: 'Proactive risk identification with automated Early Warning Notice generation.' },
      { title: 'Executive Dashboards', description: 'Portfolio-wide visibility with automated updates and one-page executive summaries.' },
    ],
    benefits: [
      'Deliver projects on time and on budget',
      'Lift project success rates',
      'Improve resource utilisation across the portfolio',
      'Cut administrative overhead',
      'Gain portfolio-wide visibility and control',
    ],
  },
];

function ServiceSection({ service, index }: { service: Service; index: number }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setIsVisible(true)),
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);

  const Icon = service.icon;
  const num = String(index + 1).padStart(2, '0');

  return (
    <section
      ref={ref}
      className={`py-20 lg:py-28 transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-x-6 gap-y-8">
          {/* Left rail: number + eyebrow + icon */}
          <div className="col-span-12 lg:col-span-3">
            <p className="font-mono text-sm font-medium text-primary tracking-[0.16em] uppercase mb-3">
              {num} / {String(services.length).padStart(2, '0')}
            </p>
            <p className="eyebrow text-white/40 mb-6">{service.tagline}</p>
            <Icon className="w-10 h-10 text-primary" strokeWidth={1.5} />
          </div>

          {/* Right: content */}
          <div className="col-span-12 lg:col-span-9">
            <h2 className="display-md text-white mb-6 max-w-3xl">{service.title}</h2>

            <p className="text-lg md:text-xl text-white/75 max-w-3xl mb-5 leading-snug">
              {service.description}
            </p>

            <p className="text-base text-white/55 max-w-3xl mb-12 leading-relaxed">
              {service.longDescription}
            </p>

            {/* Features list */}
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-px mb-12">
              {service.features.map((feature) => {
                const isFlagship = feature.title.toLowerCase().includes('flagship') || feature.title.includes('Company Brain');
                return (
                  <li key={feature.title} className="py-5 border-t border-white/10">
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <h4 className={`text-base font-bold leading-tight ${isFlagship ? 'text-primary' : 'text-white'}`}>
                        {feature.title}
                      </h4>
                    </div>
                    <p className="text-sm text-white/60 leading-relaxed">{feature.description}</p>
                  </li>
                );
              })}
            </ul>

            {/* Benefits */}
            <div className="max-w-3xl">
              <p className="eyebrow text-white/45 mb-4">Outcomes</p>
              <ul className="space-y-2">
                {service.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-baseline gap-3 text-white/75">
                    <span className="text-primary text-sm">·</span>
                    <span className="leading-relaxed">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Section divider */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-20 lg:mt-28">
        <div className="hairline" />
      </div>
    </section>
  );
}

export function ServicePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-12 lg:pb-16 w-full">
        <div className="grid grid-cols-12 gap-x-6">
          <div className="col-span-12 lg:col-span-10">
            <p className="eyebrow text-primary mb-6">Services</p>
            <h1 className="display-xl text-white mb-8">
              Domain-led delivery.
              <br className="hidden md:block" /> Not capability-led pitching.
            </h1>
            <p className="text-lg md:text-xl text-white/65 max-w-2xl leading-snug">
              Six practice areas. Every engagement starts with the project-controls problem,
              not with a deck of pillars in search of a use case.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-2 lg:text-right mt-6 lg:mt-0">
            <p className="eyebrow text-white/40">Practice areas</p>
            <p className="font-mono text-4xl md:text-5xl font-bold text-white mt-2">
              {String(services.length).padStart(2, '0')}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
        <div className="hairline" />
      </div>

      {/* WHY MOVAR REPLY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 w-full">
        <div className="grid grid-cols-12 gap-x-6 mb-10">
          <div className="col-span-12 lg:col-span-3">
            <p className="eyebrow text-primary mb-3">Why Movar Reply</p>
          </div>
          <div className="col-span-12 lg:col-span-9">
            <h2 className="display-md text-white max-w-2xl">
              Three things you do not get from a generic data consultancy.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-10">
          {[
            {
              kicker: 'Award-winning delivery',
              stat: 'PCE 2024',
              label: 'Global Innovation Winner',
              body: 'Project Controls Expo Global Innovation of the Year for our work with Severn Trent Water on AI-driven controls.',
            },
            {
              kicker: 'Native to the industry',
              stat: 'PC DNA',
              label: 'Project Controls heritage',
              body: 'Primavera P6, EVM, cost engineering, risk. We do not learn the domain on your project; we built the company on it.',
            },
            {
              kicker: 'Reply group scale',
              stat: '16,000+',
              label: 'Specialists across Reply',
              body: 'Boutique specialism from Movar Reply, with the global delivery network and sister-company capability of Reply Ltd behind us.',
            },
          ].map((item, i) => (
            <div key={item.kicker} className="border-t border-white/10 pt-6">
              <p className="eyebrow text-white/40 mb-4">
                {String(i + 1).padStart(2, '0')} · {item.kicker}
              </p>
              <p className="font-mono text-4xl md:text-5xl font-bold text-primary leading-none mb-2">
                {item.stat}
              </p>
              <p className="eyebrow text-white/55 mb-4">{item.label}</p>
              <p className="text-white/65 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
        <div className="hairline" />
      </div>

      <main className="flex-1">
        {services.map((service, index) => (
          <ServiceSection key={service.id} service={service} index={index} />
        ))}
      </main>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 w-full">
        <div className="grid grid-cols-12 gap-x-6">
          <div className="col-span-12 lg:col-span-2">
            <p className="eyebrow text-primary">Next</p>
          </div>
          <div className="col-span-12 lg:col-span-10">
            <p className="display-md text-white max-w-3xl mb-8">
              Talk to us about your programme.
            </p>
            <a href="/register" className="btn-primary">
              Register interest
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
          <p className="eyebrow text-white/40">&copy; 2026 Movar Reply</p>
          <p className="text-sm text-white/45 italic">big enough to deliver, small enough to care</p>
          <p className="eyebrow text-white/30 md:ml-auto">A Reply Group company</p>
        </div>
      </footer>
    </div>
  );
}
